import { defineStore } from "pinia";
import {
  SessionsApi,
  TrainingPlanApi,
  ItemType as ItemTypeSDK,
  CancelSessionDtoRequestActionEnum,
  type SessionsCatalogRowResponse,
} from "@simbelapp/order-sdk";
import { mergeCatalogSessions } from "~/composables/trainings/useTrainingCatalogSessions";
import { useProjectsStore } from "../project/projects.store";
import { useSessionDetailsStore } from "./session-details-store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { ISessionsStore } from "~/utils/interfaces/sessions-interfaces";
import type { ITrainingSession } from "~/utils/interfaces/trainings-interface";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFormats } from "~/composables/utils/useFormat";
import { usePagination } from "~/composables/paginations/usePagination";
import { useFilters } from "~/composables/filters/useFilters";
import { isMultiDayTimeslotError, MULTI_DAY_TIMESLOT_MESSAGE } from "~/utils/helpers/sessions/session-timeslots";

const formats = useFormats();

export const useSessionsStore = defineStore("sessionsStore", {
  state: (): ISessionsStore => {
    return {
      trainingPlanSessionIndividual: null,
      trainingPlanKpi: null,
      sessionItemSessions: null,
      sessions: null,
      showSessionModal: false,
      showCsvImportModal: false,
      showCsvExportModal: false,
      showUpdateSessionModal: false,
      showSessionStatusActionModal: {
        registered: false,
        completed: false,
        canceled: false,
        not_present: false,
        end_training: false,
        no_show: false,
        unschedule: false,
      },
      showHideKpi: false,
      showHideBlock: false,
      showModalRemoveProject: false,
      addProjectOnSessionPage: false,
      errorConfirmProject: false,
    };
  },
  getters: {},
  actions: {
    /** Drops the sessions list fetched for a training, without touching the rest of the store. */
    clearSessions() {
      this.sessions = null;
    },

    async fetchTrainingPlanKpi(currGrid: string, type: string): Promise<void> {
      const feedback = useFeedback();
      const pagination = usePagination();
      const filters = useFilters();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const data =
        type === "individual"
          ? await apiInstance.findIndividualViewTrainingsPlanKpi({
              ...queryString,
              sortingColumn: paginateOptions.sortingColumn,
              ...filtersQuery,
              itemType: currGrid as ItemTypeSDK,
            })
          : await apiInstance.findSessionViewTrainingsPlanKpi({
              ...queryString,
              sortingColumn: paginateOptions.sortingColumn,
              ...filtersQuery,
              itemType: currGrid as ItemTypeSDK,
            });

      if (data) {
        this.trainingPlanKpi = data;
      } else {
        feedback.error(`erreur`, "small");
      }
    },
    async fetchTrainingPlanSession(id: string): Promise<boolean> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      // Empty project since it's the same modal
      const projectStore = useProjectsStore();
      projectStore.trainingPlanProjectIndividual = null;

      this.trainingPlanSessionIndividual = null;
      const data = await apiInstance.findOneTrainingPlan({
        trainingPlanId: id,
      });

      if (data) {
        this.trainingPlanSessionIndividual = data;
      } else {
        feedback.error(`erreur`, "small");
      }

      return !!data;
    },
    async fetchSessionItemSessions(sessionItemId: string, trainingId: string = ""): Promise<boolean> {
      const feedback = useFeedback();
      this.sessionItemSessions = null;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
        this.sessionItemSessions = await apiInstance.findAllSessionItemAvailableSessions({
          sessionItemId,
          trainingId,
        });
        return true;
      } catch {
        feedback.error(`erreur`, "small");
        return false;
      }
    },

    async createSession(newSession: ITrainingSession) {
      const feedback = useFeedback();

      try {
        if (newSession) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
          newSession.sessionTimeslot?.forEach((timeslot) => {
            timeslot.sessionId = newSession.sessionItemId;
            timeslot.startDate = formats.toDateISOString(new Date(timeslot.startDate));
            timeslot.endDate = formats.toDateISOString(new Date(timeslot.endDate));
          });
          return await apiInstance.createSession({
            createSessionDto: {
              sessionItemId: newSession.sessionItemId ?? "",
              startDate: formats.toDateISOString(new Date()),
              endDate: formats.toDateISOString(new Date()),
              address: newSession.trainingAddress ?? "",
              designation: newSession.designation ?? "",
              maxNbAttendees: newSession.maxNbAttendees ?? 1,
              minNbAttendees: newSession.minNbAttendees ?? 1,
              overbookingNotAllowed: newSession.overbookingNotAllowed,
              ...(newSession.sessionTimeslot?.length && {
                batchCreateTimeslots: {
                  createTimeslotsDto: newSession.sessionTimeslot,
                },
              }),
            },
          });
        }
      } catch (error) {
        feedback.error(
          isMultiDayTimeslotError(error)
            ? MULTI_DAY_TIMESLOT_MESSAGE
            : `Une erreur est survenue lors de la création de la session`,
          "small",
        );
        return null;
      }
    },

    // Canceled attendees are not shown in the training plan
    // That's why we only update the state on the front
    cancelTrainingPlanIndividualSession() {
      if (this.trainingPlanSessionIndividual) {
        this.trainingPlanSessionIndividual.status = "canceled";
        this.trainingPlanSessionIndividual.allowedActions = [];
      }
    },

    async cancel(sessionId: string) {
      const data = await this.cancelSession(sessionId);
      return data;
    },

    /**
     * `throwOnError`: the historical callers read the `[]` of a failure as "no session", which is
     * harmless only where an empty list and a failed one render the same. A caller that concludes
     * something from the emptiness opts in and reports the rejection itself.
     */
    async fetchSessionsByTrainingId({
      trainingId,
      withProject,
      inProgress,
      throwOnError = false,
    }: {
      trainingId: string;
      withProject?: string;
      inProgress?: string;
      throwOnError?: boolean;
    }) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);

        const response = await apiInstance.findAllTrainingAvailableSessions({
          trainingId,
          withProjects: (withProject as string) || "true",
          inProgress: (inProgress as string) || "false",
        });

        this.sessions = response as any;

        return response;
      } catch (error) {
        if (throwOnError) throw error;
        feedback.error(`erreur`, "small");
        return [];
      }
    },

    /**
     * Ongoing and upcoming catalogue sessions of a training, split on training-plan membership:
     * an inter date only reaches the plan once someone registers on it.
     */
    async fetchCatalogSessions(trainingId: string): Promise<{
      inPlan: SessionsCatalogRowResponse[];
      notInPlan: SessionsCatalogRowResponse[];
    }> {
      const feedback = useFeedback();
      const loadError = "Une erreur est survenue lors du chargement des sessions";
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);

        // `allSettled`: one call failing must not blank out the list the other one returned,
        // or the tab claims the training has no session at all.
        const [inPlan, notInPlan] = await Promise.allSettled([
          apiInstance.getCatalogSessions({ trainingId, inPlan: true }),
          apiInstance.getCatalogSessions({ trainingId, inPlan: false }),
        ]);

        const { failed, ...sessions } = mergeCatalogSessions(inPlan, notInPlan);
        if (failed) feedback.error(loadError, "small");

        return sessions;
      } catch {
        feedback.error(loadError, "small");
        return { inPlan: [], notInPlan: [] };
      }
    },
    /**
     * Both cancels REPORT their failure instead of swallowing it.
     *
     * They used to `catch { return null }`, and `DeleteSessionConfirmModal` never looked at the
     * result — so a refused deletion closed the modal, closed the detail and refetched the grid
     * exactly like a successful one, leaving the session (and its attendees) in place with no
     * error shown. That is reachable today: a session whose émargement has been opened cannot be
     * cancelled — the backend hard-deletes its timeslots while `timeslots_signatures` still
     * references them, so both calls answer 422 (see
     * `.context/BUG-emargement-blocks-session-delete.txt`).
     */
    async cancelSessionAttendees(
      sessionsId: string[],
      notify: boolean = true,
      requestAction?: CancelSessionDtoRequestActionEnum,
      requestComment?: string,
    ) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
      try {
        return await apiInstance.cancelSessionAttendees({
          cancelSessionDto: {
            sessionsId,
            notify,
            requestAction,
            requestComment,
          },
        });
      } catch (error) {
        console.error("[SessionsStore] cancelSessionAttendees error:", error);
        feedback.error("Impossible de désinscrire les participants de la session", "small");
        return null;
      }
    },
    /** @see cancelSessionAttendees — same contract: `null` means the session was NOT cancelled. */
    async cancelSession(sessionId: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
      try {
        return await apiInstance.cancelSession({
          id: sessionId,
        });
      } catch (error) {
        console.error("[SessionsStore] cancelSession error:", error);
        feedback.error("Impossible de supprimer la session", "small");
        return null;
      }
    },
    handleSessionAttendeeDetailsModal(bool: boolean) {
      const sessionDetailsStore = useSessionDetailsStore();
      this.showSessionModal = bool;
      sessionDetailsStore.sessionViewModal = bool;
    },
  },
});
